import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * A panel over the page: a bottom sheet on the phone, a side panel (or
 * centred dialog) on wider screens. Escape, the scrim and — on the phone — a
 * downward swipe on the grabber or any element marked `data-sheet-drag`
 * close it, as an iOS sheet does.
 */
export function Sheet({
  onClose,
  children,
  side = true,
  label,
}: {
  onClose: () => void
  children: ReactNode
  /** false: centred dialog on wide screens instead of a side panel. */
  side?: boolean
  label: string
}): JSX.Element {
  const [dragY, setDragY] = useState(0)
  const start = useRef<{ y: number; t: number } | undefined>(undefined)
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    // Focus moves into the dialog for keyboard and screen-reader users.
    const previous = document.activeElement as HTMLElement | null
    if (!panel.current?.contains(document.activeElement)) panel.current?.focus({ preventScroll: true })
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      previous?.focus?.({ preventScroll: true })
    }
  }, [onClose])

  const onPointerDown = (e: React.PointerEvent): void => {
    if (window.innerWidth >= 640) return
    const target = e.target as HTMLElement
    if (!target.closest('[data-sheet-drag], .sheet-grabber') || target.closest('input, textarea, select, button')) return
    start.current = { y: e.clientY, t: Date.now() }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent): void => {
    if (!start.current) return
    setDragY(Math.max(0, e.clientY - start.current.y))
  }
  const onPointerUp = (e: React.PointerEvent): void => {
    if (!start.current) return
    const distance = e.clientY - start.current.y
    const velocity = distance / Math.max(1, Date.now() - start.current.t)
    start.current = undefined
    // A flick or a long pull closes it; anything less springs back.
    if (distance > 140 || (distance > 40 && velocity > 0.6)) onClose()
    else setDragY(0)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-stretch sm:justify-end" role="dialog" aria-modal="true" aria-label={label}>
      <button
        type="button"
        aria-label="Schließen"
        tabIndex={-1}
        className="scrim absolute inset-0 cursor-default"
        style={dragY ? { opacity: Math.max(0.2, 1 - dragY / 400) } : undefined}
        onClick={onClose}
      />
      <div
        ref={panel}
        tabIndex={-1}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          start.current = undefined
          setDragY(0)
        }}
        className={`sheet relative flex max-h-[92dvh] w-full flex-col overflow-hidden outline-none ${
          side ? 'sm:m-3 sm:max-h-none sm:w-[460px]' : 'sm:m-auto sm:max-h-[86dvh] sm:w-[560px]'
        }`}
        style={{
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transition: start.current ? 'none' : 'transform 320ms var(--ease-drawer)',
        }}
      >
        <div className="sheet-grabber" />
        {children}
      </div>
    </div>
  )
}
